import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Avatar,
  Button,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Share, Home } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useHomeIntelligenceContext } from '../../contexts/HomeIntelligenceContext';
import { getIntelligenceColors } from '../../utils/intelligenceTheme';
import CribCriticLogo from '../../assets/CribCriticLogo.svg';

/**
 * Sticky header for the Intelligence Report page that provides:
 * - CribCritic branding
 * - Property context (thumbnail + address)
 * - Navigation (back to property)
 * - Share functionality
 */
export const IntelligenceReportHeader: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const intelColors = getIntelligenceColors(isDark);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { homeId } = useParams<{ homeId: string }>();
  const { address } = useHomeIntelligenceContext();
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  // Fetch property thumbnail for visual context
  useEffect(() => {
    const fetchThumbnail = async () => {
      if (!homeId) return;
      try {
        const response = await fetch(`/api/homes/${homeId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.primary_image_url) {
            setThumbnailUrl(data.primary_image_url);
          }
        }
      } catch (error) {
        console.error('Failed to fetch thumbnail:', error);
      }
    };
    fetchThumbnail();
  }, [homeId]);

  const handleBack = () => {
    navigate(`/home/${homeId}`);
  };

  // TODO: Implement share functionality
  // Share handler will use navigator.share API or fallback to clipboard

  const headerBg = isDark
    ? `${intelColors!.background.secondary}D9` // 85% opacity
    : alpha(theme.palette.background.paper, 0.85);
  const borderColor = isDark ? intelColors!.border.primary : theme.palette.divider;
  const textPrimary = isDark ? intelColors!.text.primary : theme.palette.text.primary;
  const textSecondary = isDark ? intelColors!.text.muted : theme.palette.text.secondary;

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: headerBg,
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${borderColor}`,
          color: textPrimary,
        }}
      >
        <Toolbar sx={{ gap: { xs: 1, sm: 2 }, py: 1 }}>
          {/* Left: Logo */}
          <Box
            component="img"
            src={CribCriticLogo}
            alt="CribCritic"
            onClick={() => navigate('/homes')}
            sx={{
              height: { xs: 28, sm: 32 },
              width: 'auto',
              cursor: 'pointer',
              display: { xs: 'none', sm: 'block' },
              transition: 'opacity 0.2s',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          />

          {/* Center: Property Info */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
              justifyContent: { xs: 'flex-start', sm: 'center' },
              minWidth: 0, // Allow text truncation
            }}
          >
            {thumbnailUrl && (
              <Avatar
                src={thumbnailUrl}
                variant="rounded"
                sx={{
                  width: { xs: 40, md: 48 },
                  height: { xs: 40, md: 48 },
                  display: { xs: 'none', md: 'flex' },
                  border: `2px solid ${theme.palette.divider}`,
                }}
              >
                <Home />
              </Avatar>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: textSecondary,
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  textTransform: 'uppercase',
                  letterSpacing: { xs: 0.5, sm: 1 },
                  fontWeight: 600,
                  lineHeight: 1.2,
                }}
              >
                Intelligence Report
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.3,
                  color: textPrimary,
                }}
              >
                {address}
              </Typography>
            </Box>
          </Box>

          {/* Right: Actions */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isMobile && (
              <Tooltip title="Coming soon" arrow>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Share />}
                    disabled
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Share
                  </Button>
                </span>
              </Tooltip>
            )}
            {isMobile && (
              <Tooltip title="Coming soon" arrow>
                <span>
                  <IconButton
                    disabled
                    size="small"
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Share fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {!isMobile && (
              <Button
                variant="contained"
                size="small"
                startIcon={<ArrowBack />}
                onClick={handleBack}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Back to Property
              </Button>
            )}
            {isMobile && (
              <IconButton
                onClick={handleBack}
                color="primary"
                size="small"
                sx={{
                  border: `2px solid ${theme.palette.primary.main}`,
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default IntelligenceReportHeader;